import React from 'react';
import { Link, useLocation } from 'react-router-dom';

const Footer: React.FC = () => {
  const location = useLocation();
  
  const isCurrentPage = (path: string) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  const getFooterLinkClass = (path: string) => {
    const baseClass = "text-sm text-[#000] hover:text-[#0B6054] transition-colors duration-300 relative group inline-block";
    const currentClass = isCurrentPage(path) ? "font-bold" : "font-medium";
    return `${baseClass} ${currentClass}`;
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <footer className="mx-auto px-4 md:px-10 lg:px-32 xl:px-64 w-full bg-[#F6F6F6] pt-10 pb-10">
      <div className="">
        <div className="grid md:grid-cols-3 gap-20 lg:gap-32 xl:gap-40">
          {/* Logo and Description - Left Side */}
          <div className="space-y-10">
            <div className="flex items-center space-x-3">
              <Link to="/" onClick={scrollToTop}>
                <img 
                  src="/images/Logo.webp" 
                  alt="The Wealthy Miner Logo" 
                  className="h-20 w-auto"
                />
              </Link>
            </div>
            <p className="text-sm text-[#000] leading-relaxed lg:w-[300px] w-full">
              Your gateway to potentially <strong>life-changing returns</strong> in junior mining stocks. 
              Led by Robert Bruggeman, a renowned former Bay Street research analyst and trader.
            </p>
          </div>

          {/* Useful Links - Middle */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-[#000] mb-4">Useful Links</h3>
            <ul className="space-y-2">
              <li>
                <Link to="/mining-stocks" className={getFooterLinkClass('/mining-stocks')} onClick={scrollToTop}>
                  <span>Top Mining Stocks</span>
                  <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-[#0B6054] transition-all duration-300 group-hover:w-full -bottom-1"></span>
                </Link>
              </li>
              <li>
                <Link to="/research" className={getFooterLinkClass('/research')} onClick={scrollToTop}>
                  <span>Research</span>
                  <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-[#0B6054] transition-all duration-300 group-hover:w-full -bottom-1"></span>
                </Link>
              </li>
              <li>
                <Link to="/resources" className={getFooterLinkClass('/resources')} onClick={scrollToTop}>
                  <span>Resources</span>
                  <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-[#0B6054] transition-all duration-300 group-hover:w-full -bottom-1"></span>
                </Link>
              </li>
              <li>
                <Link to="/about" className={getFooterLinkClass('/about')} onClick={scrollToTop}>
                  <span>About Us</span>
                  <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-[#0B6054] transition-all duration-300 group-hover:w-full -bottom-1"></span>
                </Link>
              </li>
              <li>
                <Link to="/interviews" className={getFooterLinkClass('/interviews')} onClick={scrollToTop}>
                  <span>Interviews</span>
                  <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-[#0B6054] transition-all duration-300 group-hover:w-full -bottom-1"></span>
                </Link>
              </li>
              <li>
                <Link to="/blog" className={getFooterLinkClass('/blog')} onClick={scrollToTop}>
                  <span>Blog</span>
                  <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-[#0B6054] transition-all duration-300 group-hover:w-full -bottom-1"></span>
                </Link>
              </li>
              <li>
                <Link to="/contact" className={getFooterLinkClass('/contact')} onClick={scrollToTop}>
                  <span>Contact Us</span>
                  <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-[#0B6054] transition-all duration-300 group-hover:w-full -bottom-1"></span>
                </Link>
              </li>
            </ul>
          </div>

          {/* Social Media and Legal - Right Side */}
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-[#000] mb-4">Social Media</h3>
              <div className="flex lg:space-x-4 space-x-1">
                {/* LinkedIn */}
                <a href="https://www.linkedin.com/company/the-wealthy-miner/" target="_blank" rel="noopener noreferrer" className="w-8 h-8 md:w-10 md:h-10 lg:w-12 lg:h-12 flex items-center justify-center hover:scale-110 transition-transform duration-200" aria-label="Follow us on LinkedIn">
                  <img src="/images/linkedin.webp" alt="LinkedIn" className="w-6 h-6 md:w-5 md:h-5 lg:w-8 lg:h-8" />
                </a>
                {/* Facebook */}
                <a href="https://www.facebook.com/people/The-Wealthy-Miner/61579338600099/" target="_blank" rel="noopener noreferrer" className="w-8 h-8 md:w-10 md:h-10 lg:w-12 lg:h-12 flex items-center justify-center hover:scale-110 transition-transform duration-200" aria-label="Follow us on Facebook">
                  <img src="/images/facebook.webp" alt="Facebook" className="w-5 h-5 md:w-5 md:h-5 lg:w-6 lg:h-6" />
                </a>
                {/* X (Twitter) */}
                <a href="https://x.com/WealthyMinerCom" target="_blank" rel="noopener noreferrer" className="w-8 h-8 md:w-10 md:h-10 lg:w-12 lg:h-12 flex items-center justify-center hover:scale-110 transition-transform duration-200" aria-label="Follow us on X (Twitter)">
                  <img src="/images/x.webp" alt="X (Twitter)" className="w-5 h-5 md:w-5 md:h-5 lg:w-6 lg:h-6" />
                </a>
                {/* YouTube */}
                <a href="https://www.youtube.com/@TheWealthyMiner" target="_blank" rel="noopener noreferrer" className="w-8 h-8 md:w-10 md:h-10 lg:w-12 lg:h-12 flex items-center justify-center hover:scale-110 transition-transform duration-200" aria-label="Subscribe to our YouTube channel">
                  <img src="/images/youtube.webp" alt="YouTube" className="w-5 h-5 md:w-5 md:h-5 lg:w-7 lg:h-7" />
                </a>
              </div>
            </div>
            
            <div>
              <ul className="space-y-2">
                <li><a href="/terms" className="text-sm text-gray-[#000] hover:text-gray-[#000] transition-colors">Terms & Conditions</a></li>
                <li><a href="/privacy" className="text-sm text-gray-[#000] hover:text-gray-[#000] transition-colors">Privacy Policy</a></li>
                <li><a href="/copyright" className="text-sm text-gray-[#000] hover:text-gray-[#000] transition-colors">Copyright</a></li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
